/**
 * Pure builders for the TEK_RSI_MOMENTUM Trade Review charts (no React, no
 * canvas) so marker placement, levels and CSV rows are unit-testable.
 *
 * Both charts share the same category x-axis (the session's 5-minute slot
 * labels, IST "HH:MM") so a visible window [start, end] keeps them linked.
 * Everything drawn comes from the API's stored data; a missing candle stays
 * an empty slot (null) -- never interpolated.
 */
import { istText } from "@/utils/istTime";
import type { Plugin } from "chart.js";
import type { RsiTradeReview } from "@/types";

export const C = {
  up: "#1B5E20",
  down: "#C62828",
  wick: "#616161",
  vwap: "#F57C00",
  ema20: "#1565C0",
  rsi: "#6A1B9A",
  rsiEma: "#00897B",
  rsiWma: "#EF6C00",
  mid: "#9E9E9E",
  entry: "#455A64",
  stop: "#C62828",
  target: "#2E7D32",
  firstTarget: "#66BB6A",
  buy: "#2E7D32",
  sell: "#C62828",
  exit: "#212121",
  raw: "#78909C",
  blocked: "#9E9E9E",
  warning: "#ED6C02",
  profitZone: "rgba(46,125,50,0.10)",
  lossZone: "rgba(198,40,40,0.10)",
} as const;

export interface Toggles {
  vwap: boolean;
  ema20: boolean;
  rsi: boolean;
  levels: boolean;
  rawCrosses: boolean;
}

export const DEFAULT_TOGGLES: Toggles = { vwap: true, ema20: true, rsi: true, levels: true, rawCrosses: true };

export interface ChartLabel {
  index: number;
  value: number;
  text: string;
  color: string;
  position: "above" | "below";
}

export interface Window {
  start: number;
  end: number;
}

/** Default view: the trade with ~12 bars of context each side. */
export function focusWindow(r: RsiTradeReview): Window {
  const n = r.candles.length;
  if (n === 0) return { start: 0, end: 0 };
  const a = r.markers.entry.signal_index ?? r.markers.entry.index ?? 0;
  const b = r.markers.exit?.index ?? r.levels.to_index ?? a;
  return { start: Math.max(0, Math.min(a, b) - 12), end: Math.min(n - 1, Math.max(a, b) + 12) };
}

export function zoom(w: Window, n: number, factor: number): Window {
  const width = Math.max(4, Math.round((w.end - w.start + 1) * factor));
  const mid = (w.start + w.end) / 2;
  let start = Math.round(mid - width / 2);
  start = Math.max(0, Math.min(start, Math.max(0, n - width)));
  return { start, end: Math.min(n - 1, start + width - 1) };
}

export function pan(w: Window, n: number, by: number): Window {
  const width = w.end - w.start;
  const start = Math.max(0, Math.min(w.start + by, Math.max(0, n - 1 - width)));
  return { start, end: Math.min(n - 1, start + width) };
}

const blank = (n: number): (number | null)[] => Array.from({ length: n }, () => null);

/** One-point series: value at `index`, null elsewhere (marker datasets). */
export function pointAt(n: number, index: number | null | undefined, value: number | null | undefined): (number | null)[] {
  const out = blank(n);
  if (index !== null && index !== undefined && index >= 0 && index < n && value !== null && value !== undefined) out[index] = value;
  return out;
}

/** Horizontal level drawn only across the trade's bars (entry .. exit). */
export function levelLine(n: number, from: number | null, to: number | null, value: number | null): (number | null)[] {
  const out = blank(n);
  if (from === null || value === null) return out;
  const last = Math.min(n - 1, to ?? n - 1);
  for (let i = Math.max(0, from); i <= last; i++) out[i] = value;
  return out;
}

/* eslint-disable @typescript-eslint/no-explicit-any -- Chart.js mixed
 * bar/line/marker datasets (per-dataset type overrides, fill targets) are
 * typed looser here, the same convention Rsi2CandleChart/PayoffChart use. */
export function buildPriceChart(r: RsiTradeReview, t: Toggles): { data: any; labels: ChartLabel[] } {
  const n = r.candles.length;
  const labels = r.candles.map((c) => c.time);
  const isLong = r.summary.side === "LONG";
  const lows = r.candles.map((c) => c.low).filter((v): v is number => v !== null);
  const floor = lows.length ? Math.min(...lows) : null;
  const ds: any[] = [
    { type: "bar", label: "Wick", data: r.candles.map((c) => (c.missing ? null : [c.low, c.high])), backgroundColor: C.wick, barThickness: 1.5, order: 5 },
    {
      type: "bar", label: "NIFTY 5m", order: 4, barThickness: 7,
      data: r.candles.map((c) => (c.missing || c.open === null || c.close === null ? null : [Math.min(c.open, c.close), Math.max(c.open, c.close) + (c.open === c.close ? 0.05 : 0)])),
      backgroundColor: r.candles.map((c) => (c.close !== null && c.open !== null && c.close >= c.open ? C.up : C.down)),
    },
  ];
  if (t.vwap) ds.push({ type: "line", label: "VWAP", data: r.series.vwap, borderColor: C.vwap, borderWidth: 1.5, pointRadius: 0, spanGaps: false, order: 3 });
  if (t.ema20) ds.push({ type: "line", label: "EMA(20)", data: r.series.ema20, borderColor: C.ema20, borderWidth: 1.5, pointRadius: 0, spanGaps: false, order: 3 });
  const lv = r.levels;
  if (t.levels && lv.entry !== null) {
    const entryIdx = ds.length;
    ds.push({ type: "line", label: "Entry", data: levelLine(n, lv.from_index, lv.to_index, lv.entry), borderColor: C.entry, borderDash: [4, 3], borderWidth: 1.25, pointRadius: 0, order: 2 });
    ds.push({ type: "line", label: "Target", data: levelLine(n, lv.from_index, lv.to_index, lv.target), borderColor: C.target, borderWidth: 1.5, pointRadius: 0, order: 2, fill: { target: entryIdx }, backgroundColor: C.profitZone });
    ds.push({ type: "line", label: "Stop-loss", data: levelLine(n, lv.from_index, lv.to_index, lv.initial_stop), borderColor: C.stop, borderWidth: 1.5, pointRadius: 0, order: 2, fill: { target: entryIdx }, backgroundColor: C.lossZone });
    if (lv.final_stop !== null && lv.initial_stop !== null && Math.abs(lv.final_stop - lv.initial_stop) > 1e-6) {
      ds.push({ type: "line", label: `Final stop (${r.summary.stop_kind.toLowerCase()})`, data: levelLine(n, lv.from_index, lv.to_index, lv.final_stop), borderColor: C.stop, borderDash: [2, 2], borderWidth: 1, pointRadius: 0, order: 2 });
    }
    if (lv.first_target !== null) {
      ds.push({ type: "line", label: "First target", data: levelLine(n, lv.from_index, lv.to_index, lv.first_target), borderColor: C.firstTarget, borderDash: [5, 3], borderWidth: 1, pointRadius: 0, order: 2 });
    }
  }
  const e = r.markers.entry;
  const entryCandle = e.index !== null ? r.candles[e.index] : undefined;
  const entryY = entryCandle && !entryCandle.missing ? (isLong ? entryCandle.low : entryCandle.high) : e.price;
  ds.push({
    type: "line", label: isLong ? "BUY (algo entry)" : "SELL (algo entry)", data: pointAt(n, e.index, entryY), showLine: false,
    pointStyle: "triangle", rotation: isLong ? 0 : 180, pointRadius: 9, pointBackgroundColor: isLong ? C.buy : C.sell, borderColor: isLong ? C.buy : C.sell, order: 0,
  });
  const x = r.markers.exit;
  if (x) {
    ds.push({ type: "line", label: "Exit", data: pointAt(n, x.index, x.price), showLine: false, pointStyle: "rectRot", pointRadius: 7, pointBackgroundColor: C.exit, borderColor: "#fff", order: 0 });
  }
  if (r.markers.partials.length) {
    const pd = blank(n);
    r.markers.partials.forEach((p) => { if (p.index !== null && lv.first_target !== null) pd[p.index] = lv.first_target; });
    ds.push({ type: "line", label: "Partial booking", data: pd, showLine: false, pointStyle: "circle", pointRadius: 5, pointBackgroundColor: C.firstTarget, order: 0 });
  }
  if (r.markers.warnings.length && floor !== null) {
    const wd = blank(n);
    r.markers.warnings.forEach((w) => { if (w.index !== null && w.index < n) wd[w.index] = floor; });
    ds.push({ type: "line", label: "Data warning", data: wd, showLine: false, pointStyle: "triangle", pointRadius: 6, pointBackgroundColor: C.warning, order: 0 });
  }
  const textLabels: ChartLabel[] = [];
  if (e.index !== null && entryY !== null && entryY !== undefined) {
    textLabels.push({ index: e.index, value: entryY, text: `${e.label} ${e.time ?? ""}`, color: isLong ? C.buy : C.sell, position: isLong ? "below" : "above" });
  }
  if (x && x.index !== null && x.price !== null) {
    textLabels.push({ index: x.index, value: x.price, text: `EXIT ${x.reason ?? ""}`, color: C.exit, position: isLong ? "above" : "below" });
  }
  return { data: { labels, datasets: ds }, labels: textLabels };
}

export function buildRsiChart(r: RsiTradeReview, t: Toggles): { data: any; labels: ChartLabel[] } {
  const n = r.candles.length;
  const s = r.series;
  const ds: any[] = [
    { type: "line", label: "RSI(9)", data: s.rsi, borderColor: C.rsi, borderWidth: 1.5, pointRadius: 0, spanGaps: false },
    { type: "line", label: "EMA(3) of RSI", data: s.rsi_ema, borderColor: C.rsiEma, borderWidth: 1.25, pointRadius: 0, spanGaps: false },
    { type: "line", label: "WMA(21) of RSI", data: s.rsi_wma, borderColor: C.rsiWma, borderWidth: 1.25, pointRadius: 0, spanGaps: false },
    { type: "line", label: "RSI 50", data: Array.from({ length: n }, () => 50), borderColor: C.mid, borderDash: [4, 4], borderWidth: 1, pointRadius: 0 },
  ];
  if (t.rawCrosses) {
    const raw = blank(n);
    r.markers.raw_crossovers.forEach((x) => { raw[x.index] = x.rsi_ema; });
    ds.push({ type: "line", label: "Raw Hilega-Milega cross", data: raw, showLine: false, pointStyle: "circle", pointRadius: 4, pointBackgroundColor: C.raw, borderColor: C.raw });
  }
  const isLong = r.summary.side === "LONG";
  const si = r.markers.entry.signal_index;
  ds.push({
    type: "line", label: "Algo entry signal", data: pointAt(n, si, si !== null ? s.rsi_ema[si] : null), showLine: false,
    pointStyle: "triangle", rotation: isLong ? 0 : 180, pointRadius: 9, pointBackgroundColor: isLong ? C.buy : C.sell,
  });
  const blocked = blank(n);
  r.markers.signals.filter((x) => x.status === "BLOCKED" && x.index !== null).forEach((x) => { blocked[x.index as number] = s.rsi[x.index as number] ?? 50; });
  ds.push({ type: "line", label: "Blocked signal", data: blocked, showLine: false, pointStyle: "crossRot", pointRadius: 7, borderWidth: 2, borderColor: C.blocked, pointBorderColor: C.blocked });
  const x = r.markers.exit;
  if (x && x.index !== null) {
    ds.push({ type: "line", label: "Exit", data: pointAt(n, x.index, s.rsi[x.index] ?? 50), showLine: false, pointStyle: "rectRot", pointRadius: 6, pointBackgroundColor: C.exit });
  }
  const textLabels: ChartLabel[] = [];
  if (si !== null && s.rsi_ema[si] !== null) {
    textLabels.push({ index: si, value: s.rsi_ema[si] as number, text: "ENTRY SIGNAL", color: isLong ? C.buy : C.sell, position: isLong ? "below" : "above" });
  }
  r.markers.signals.filter((y) => y.status === "BLOCKED" && y.index !== null).forEach((y) => {
    textLabels.push({ index: y.index as number, value: s.rsi[y.index as number] ?? 50, text: `BLOCKED: ${y.reason ?? "?"}`, color: C.blocked, position: "above" });
  });
  return { data: { labels: r.candles.map((c) => c.time), datasets: ds }, labels: textLabels };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Draws short text labels next to markers (only inside the visible window). */
export function buildLabelPlugin(id: string): Plugin {
  return {
    id,
    afterDatasetsDraw: (chart) => {
      const labels = ((chart.options.plugins as unknown as Record<string, { labels?: ChartLabel[] }>)?.[id]?.labels) ?? [];
      const { ctx, scales, chartArea } = chart;
      const xs = scales.x;
      const ys = scales.y;
      if (!xs || !ys || !chartArea) return;
      ctx.save();
      ctx.font = "600 11px sans-serif";
      ctx.textAlign = "center";
      for (const l of labels) {
        const px = xs.getPixelForValue(l.index);
        const py = ys.getPixelForValue(l.value);
        if (px < chartArea.left || px > chartArea.right || py < chartArea.top - 20 || py > chartArea.bottom + 20) continue;
        ctx.fillStyle = l.color;
        ctx.fillText(l.text, px, l.position === "above" ? py - 14 : py + 22);
      }
      ctx.restore();
    },
  };
}

/** Per-candle rows for the CSV export (the trade summary repeated on each row is avoided: summary first, then bars). */
export function csvRows(r: RsiTradeReview): Array<Record<string, unknown>> {
  const s = r.summary;
  const head: Record<string, unknown> = {
    row: "TRADE", time_ist: s.entry_time_ist, side: s.side, mode: s.mode, entry: s.entry_price, stop_loss: s.initial_stop,
    target: s.target, exit_time_ist: s.exit_time_ist, exit: s.exit_price, exit_reason: s.exit_reason, realized_pnl: s.realized_pnl,
    r_multiple: s.r_multiple, contract: s.option.contract, entry_premium: s.option.entry_premium, exit_premium: s.option.exit_premium,
    open: "", high: "", low: "", close: "", vwap: "", ema20: "", rsi: "", rsi_ema3: "", rsi_wma21: "", marker: "",
  };
  const markerAt = (i: number): string => {
    const m: string[] = [];
    if (r.markers.entry.index === i) m.push(r.markers.entry.label);
    if (r.markers.entry.signal_index === i) m.push("SIGNAL_BAR");
    if (r.markers.exit?.index === i) m.push(`EXIT:${r.markers.exit.reason}`);
    r.markers.raw_crossovers.filter((x) => x.index === i).forEach((x) => m.push(`RAW_CROSS_${x.direction}`));
    r.markers.signals.filter((x) => x.index === i && x.status === "BLOCKED").forEach((x) => m.push(`BLOCKED:${x.reason}`));
    if (r.candles[i]?.missing) m.push("MISSING_CANDLE");
    return m.join(" ");
  };
  const rows = r.candles.map((c, i) => ({
    ...Object.fromEntries(Object.keys(head).map((k) => [k, ""])),
    row: "BAR", time_ist: c.start_ist, open: c.open, high: c.high, low: c.low, close: c.close,
    vwap: r.series.vwap[i], ema20: r.series.ema20[i], rsi: r.series.rsi[i], rsi_ema3: r.series.rsi_ema[i],
    rsi_wma21: r.series.rsi_wma[i], marker: markerAt(i),
  }));
  return [head, ...rows];
}

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2, minimumFractionDigits: 2 });
export const inr = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : INR.format(v));
export const num = (v: number | null | undefined, dp = 2): string =>
  v === null || v === undefined ? "—" : v.toLocaleString("en-IN", { minimumFractionDigits: dp, maximumFractionDigits: dp });
/** Any API timestamp -> "YYYY-MM-DD HH:MM:SS IST" (offset-bearing values are converted to IST). */
export function istLabel(iso: string | null | undefined): string {
  return istText(iso);
}

/** Price-axis range for the visible window (candles + shown overlays + levels), padded; never forced to 0. */
export function priceRange(r: RsiTradeReview, w: Window, t: Toggles): { min: number; max: number } | null {
  const vals: number[] = [];
  for (let i = w.start; i <= w.end && i < r.candles.length; i++) {
    const c = r.candles[i];
    if (c.low !== null) vals.push(c.low);
    if (c.high !== null) vals.push(c.high);
    if (t.vwap && r.series.vwap[i] !== null) vals.push(r.series.vwap[i] as number);
    if (t.ema20 && r.series.ema20[i] !== null) vals.push(r.series.ema20[i] as number);
  }
  if (t.levels) {
    for (const v of [r.levels.entry, r.levels.initial_stop, r.levels.final_stop, r.levels.target, r.levels.first_target]) {
      if (v !== null) vals.push(v);
    }
  }
  if (!vals.length) return null;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = Math.max((hi - lo) * 0.08, 2);
  return { min: Math.floor((lo - pad) / 5) * 5, max: Math.ceil((hi + pad) / 5) * 5 };   // round to 5 pts for clean ticks
}
